import React, { useState, useEffect } from 'react';
import { X, Bell, AlertTriangle, CheckCircle, Info, AlertCircle } from 'lucide-react';
import { defontanaApi, MensajeInbox } from '../../services/defontanaApi';

interface InboxProps {
  isOpen: boolean;
  onClose: () => void;
}

const Inbox: React.FC<InboxProps> = ({ isOpen, onClose }) => {
  const [mensajes, setMensajes] = useState<MensajeInbox[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarMensajes = async () => {
      try {
        const data = await defontanaApi.getMensajesInbox();
        setMensajes(data);
      } catch (error) {
        console.error('Error al cargar mensajes:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      cargarMensajes();
    }
  }, [isOpen]);

  const getIcon = (tipo: string) => {
    switch (tipo) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBgColor = (tipo: string) => {
    switch (tipo) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const marcarComoLeido = async (id: string) => {
    try {
      await defontanaApi.marcarMensajeComoLeido(id);
      setMensajes(prev => 
        prev.map(msg => msg.id === id ? { ...msg, leido: true } : msg)
      );
    } catch (error) {
      console.error('Error al marcar mensaje como leído:', error);
    }
  };

  const mensajesNoLeidos = mensajes.filter(msg => !msg.leido).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Inbox</h2>
            {mensajesNoLeidos > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {mensajesNoLeidos}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : mensajes.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500">
              <Bell className="w-8 h-8 mb-2" />
              <p>No hay mensajes</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {mensajes.map((mensaje) => (
                <div
                  key={mensaje.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    mensaje.leido 
                      ? 'bg-white border-gray-200 hover:bg-gray-50' 
                      : `${getBgColor(mensaje.tipo)} font-medium`
                  }`}
                  onClick={() => marcarComoLeido(mensaje.id)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getIcon(mensaje.tipo)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {mensaje.titulo}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {new Date(mensaje.fecha).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-600 line-clamp-2">
                        {mensaje.mensaje}
                      </p>
                    </div>
                    {!mensaje.leido && (
                      <div className="flex-shrink-0">
                        <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <button className="w-full text-center text-sm text-primary-600 hover:text-primary-700 font-medium">
            Ver todos los mensajes
          </button>
        </div>
      </div>
    </div>
  );
};

export default Inbox;
